import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import StylishNewInput from 'components/DesignSystems/New/StylishNewInput';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import ICSWidget from './ICSWidget';
import { ConditionalCollapsibleCard } from 'components/IAP/ConditionalCollapsibleCard';
import { getIncidentCardTitle } from '../USCGPage';
import {
  ComsListItem,
  useIncidentComsList,
  useCreateComsListItem,
  useUpdateComsListItem,
  useDeleteComsListItem,
} from 'components/IAP/hooks/incidentComsListHooks';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  useFutureOperationalPeriodNumber,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { NULL_VALUE } from 'constants/displayConstants';

type Editable205AFields = {
  workGroupId: string;
  name: string;
  cellPhoneNumber: string;
  rxFrequency: string;
  other: string;
};

const defaultFields: Editable205AFields = {
  workGroupId: '',
  name: '',
  cellPhoneNumber: '',
  rxFrequency: '',
  other: '',
};

const cellInputClass = 'form-control form-control-sm';

const ComsRowEdit: React.FC<{
  row: ComsListItem;
  workGroupOptions: { label: string; value: string }[];
  onSave: (
    id: string,
    draft: Editable205AFields,
    archived: boolean
  ) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}> = ({ row, workGroupOptions, onSave, onCancel, isSaving }) => {
  const [draft, setDraft] = useState<Editable205AFields>({
    workGroupId: row.workGroupId ?? '',
    name: row.name ?? '',
    cellPhoneNumber: row.cellPhoneNumber ?? '',
    rxFrequency: row.rxFrequency ?? '',
    other: row.other ?? '',
  });

  const handleChange = (field: keyof Editable205AFields, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <tr>
      <td>
        <PratusSelect
          options={workGroupOptions}
          value={
            workGroupOptions.find((o) => o.value === draft.workGroupId) ?? null
          }
          onChange={(opt) =>
            handleChange(
              'workGroupId',
              opt && !Array.isArray(opt)
                ? String((opt as OptionType).value)
                : ''
            )
          }
          isClearable
          isSearchable
          classes="sml"
          menuPortalTarget={document.body}
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('name', e.target.value)
          }
          placeholder="Contact name"
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.cellPhoneNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('cellPhoneNumber', e.target.value)
          }
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.rxFrequency}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('rxFrequency', e.target.value)
          }
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.other}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('other', e.target.value)
          }
        />
      </td>
      <td style={{ textAlign: 'right' }}>
        <div className="d-flex justify-content-end gap-2">
          <StylishNewButton
            className="button button--icon"
            type="button"
            onClick={onCancel}
            title="Cancel"
          >
            <SharedIcon iconName="close" size="sm" />
          </StylishNewButton>
          <StylishNewButton
            className="button button--icon"
            type="button"
            onClick={() => void onSave(row.comsListItemId, draft, row.archived)}
            disabled={isSaving}
            title="Save"
          >
            <SharedIcon iconName="check" size="sm" color="success" />
          </StylishNewButton>
        </div>
      </td>
    </tr>
  );
};

// ── Per-row view component ───────────────────────────────────────────
const ComsRowView: React.FC<{
  row: ComsListItem;
  workGroupLabel: string;
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}> = ({ row, workGroupLabel, canEdit, onEdit, onDelete }) => (
  <tr>
    <td>{workGroupLabel}</td>
    <td>{row.name || NULL_VALUE}</td>
    <td>{row.cellPhoneNumber || NULL_VALUE}</td>
    <td>{row.rxFrequency || NULL_VALUE}</td>
    <td>{row.other || NULL_VALUE}</td>
    {canEdit && (
      <td style={{ textAlign: 'right' }}>
        <div className="d-flex justify-content-end gap-2">
          <StylishNewButton
            className="button button--link button--sm"
            type="button"
            onClick={onEdit}
            title="Edit"
          >
            <SharedIcon iconName="edit" color="light" />
          </StylishNewButton>
          <StylishNewButton
            className="button button--link button--sm"
            type="button"
            onClick={onDelete}
            title="Delete"
          >
            <SharedIcon iconName="delete" color="light" />
          </StylishNewButton>
        </div>
      </td>
    )}
  </tr>
);

// ── Per-row container – switches between view / edit ─────────────────
const ComsRowContainer: React.FC<{
  row: ComsListItem;
  workGroupOptions: { label: string; value: string }[];
  canEdit: boolean;
  onSave: (
    id: string,
    draft: Editable205AFields,
    archived: boolean
  ) => Promise<void>;
  onDelete: (id: string) => void;
  isSaving: boolean;
}> = ({ row, workGroupOptions, canEdit, onSave, onDelete, isSaving }) => {
  const [isEditing, setIsEditing] = useState(false);

  const workGroupLabel =
    workGroupOptions.find((o) => o.value === row.workGroupId)?.label ?? '—';

  if (isEditing && canEdit) {
    return (
      <ComsRowEdit
        row={row}
        workGroupOptions={workGroupOptions}
        onSave={async (id, draft, archived) => {
          await onSave(id, draft, archived);
          setIsEditing(false);
        }}
        onCancel={() => setIsEditing(false)}
        isSaving={isSaving}
      />
    );
  }

  return (
    <ComsRowView
      row={row}
      workGroupLabel={workGroupLabel}
      canEdit={canEdit}
      onEdit={() => setIsEditing(true)}
      onDelete={() => onDelete(row.comsListItemId)}
    />
  );
};

// ── Add-row component – also owns its own draft ──────────────────────
const ComsRowAdd: React.FC<{
  workGroupOptions: { label: string; value: string }[];
  onCreate: (draft: Editable205AFields) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}> = ({ workGroupOptions, onCreate, onCancel, isSaving }) => {
  const [draft, setDraft] = useState<Editable205AFields>({ ...defaultFields });

  const handleChange = (field: keyof Editable205AFields, value: string) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <tr>
      <td>
        <PratusSelect
          options={workGroupOptions}
          value={
            workGroupOptions.find((o) => o.value === draft.workGroupId) ?? null
          }
          onChange={(opt) =>
            handleChange(
              'workGroupId',
              opt && !Array.isArray(opt)
                ? String((opt as OptionType).value)
                : ''
            )
          }
          isClearable
          isSearchable
          menuPortalTarget={document.body}
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('name', e.target.value)
          }
          placeholder="Contact name"
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.cellPhoneNumber}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('cellPhoneNumber', e.target.value)
          }
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.rxFrequency}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('rxFrequency', e.target.value)
          }
        />
      </td>
      <td>
        <StylishNewInput
          className={cellInputClass}
          value={draft.other}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleChange('other', e.target.value)
          }
        />
      </td>
      <td style={{ textAlign: 'right' }}>
        <div className="d-flex justify-content-end gap-2">
          <StylishNewButton
            className="button button--icon"
            type="button"
            onClick={onCancel}
            title="Cancel"
          >
            <SharedIcon iconName="close" size="sm" />
          </StylishNewButton>
          <StylishNewButton
            className="button button--icon"
            type="button"
            onClick={() => void onCreate(draft)}
            disabled={isSaving}
            title="Save"
          >
            <SharedIcon iconName="check" size="sm" color="success" />
          </StylishNewButton>
        </div>
      </td>
    </tr>
  );
};

// ── Main component ───────────────────────────────────────────────────
const CommunicationsList: React.FC = () => {
  const [searchParams] = useSearchParams();
  const phase = searchParams.get('phase');
  const mode = searchParams.get('mode');
  const sectionKey = `${phase}-${mode}-communications-list`;

  const { exportAsForm } = useIcsFormPdfPreview();
  const futurePeriodNumber = useFutureOperationalPeriodNumber();

  const { data: incidentPermissions } = useIncidentPermissions();
  const canEditCards = incidentPermissions?.['incident:editCards'] ?? false;

  const { data: communications, isLoading, error } = useIncidentComsList();
  const { data: roster } = useGetPositionsForIncident();
  const createComsMutation = useCreateComsListItem();
  const updateComsMutation = useUpdateComsListItem();
  const deleteComsMutation = useDeleteComsListItem();
  const isSaving =
    createComsMutation.isPending ||
    updateComsMutation.isPending ||
    deleteComsMutation.isPending;

  const workGroups = useMemo(
    () =>
      roster
        ?.filter((pos) => pos.is_workgroup)
        .map((wg) => ({ id: wg.id, name: wg.name })) ?? [],
    [roster]
  );
  const workGroupOptions = useMemo(
    () => workGroups.map((g) => ({ label: g.name, value: g.id })),
    [workGroups]
  );

  const [isAdding, setIsAdding] = useState(false);
  const [search, setSearch] = useState('');

  const activeRows = useMemo(() => communications ?? [], [communications]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return activeRows;
    return activeRows.filter((r) =>
      (r.name ?? '').toLowerCase().includes(term)
    );
  }, [activeRows, search]);

  const handleSaveEdit = async (
    id: string,
    draft: Editable205AFields,
    archived: boolean
  ) => {
    await updateComsMutation.mutateAsync({
      id,
      data: {
        workGroupId: draft.workGroupId || null,
        name: draft.name || null,
        cellPhoneNumber: draft.cellPhoneNumber || null,
        rxFrequency: draft.rxFrequency || null,
        other: draft.other || null,
        archived,
      },
    });
  };

  const handleDelete = async (id: string) => {
    await deleteComsMutation.mutateAsync(id);
  };

  const handleCreate = async (draft: Editable205AFields) => {
    await createComsMutation.mutateAsync({
      workGroupId: draft.workGroupId || null,
      name: draft.name || null,
      cellPhoneNumber: draft.cellPhoneNumber || null,
      rxFrequency: draft.rxFrequency || null,
      other: draft.other || null,
    });
    setIsAdding(false);
  };

  const handleExport205A = () => {
    void exportAsForm({
      formname: 'ICS_205A_CG',
      mapSketch: null,
      periodNumber: futurePeriodNumber,
    });
  };

  return (
    <ConditionalCollapsibleCard
      sectionKey={sectionKey}
      className="planning-widget"
      contentClassName="content mt-2"
      headerLeft={
        <ICSWidget.HeaderLeft
          iconName="bolt"
          iconColor="primary"
          title={getIncidentCardTitle('communicationsList')}
          subtitle=""
        />
      }
      headerRight={
        <ICSWidget.HeaderRight
          icsRoles={['IC/UC', 'PSC', 'Situation Unit Leader']}
          handleExport={handleExport205A}
          exportText="Preview ICS-205A"
        />
      }
    >
      {isLoading && (
        <div className="py-4 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}
      {error && (
        <div className="alert alert-danger">
          Error loading communications: {error.message}
        </div>
      )}
      {!isLoading && !error && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2">
              <span className="text-nowrap">
                Basic Local Communications Information:
              </span>
              <StylishNewInput
                className="form-control"
                placeholder="Search by name..."
                value={search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearch(e.target.value)
                }
              />
            </div>
            {canEditCards && (
              <StylishNewButton
                className="button button--primary button--sm"
                type="button"
                onClick={() => setIsAdding(true)}
                disabled={isAdding}
              >
                <SharedIcon iconName="add" classes="me-2" />
                Add Contact
              </StylishNewButton>
            )}
          </div>
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th>Name</th>
                  <th>Cell Phone #</th>
                  <th>Radio Frequency</th>
                  <th>Other</th>
                  {canEditCards && <th />}
                </tr>
              </thead>
              <tbody>
                {filteredRows.length === 0 && !isAdding && (
                  <tr>
                    <td colSpan={canEditCards ? 6 : 5} className="no-data">
                      No communications configured
                    </td>
                  </tr>
                )}
                {filteredRows.map((row) => (
                  <ComsRowContainer
                    key={row.comsListItemId}
                    row={row}
                    workGroupOptions={workGroupOptions}
                    canEdit={canEditCards}
                    onSave={handleSaveEdit}
                    onDelete={(id) => void handleDelete(id)}
                    isSaving={isSaving}
                  />
                ))}
                {isAdding && (
                  <ComsRowAdd
                    workGroupOptions={workGroupOptions}
                    onCreate={handleCreate}
                    onCancel={() => setIsAdding(false)}
                    isSaving={isSaving}
                  />
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </ConditionalCollapsibleCard>
  );
};

export default CommunicationsList;
